/*
 * Выбор номера для исходящего звонка: группировка по провайдерам, номер по умолчанию,
 * подпись «С номера», форматирование и SIP-заголовки звонка.
 *
 * Чистые функции без Vue. CommonJS (module.exports) — default-import в компоненты,
 * require в Node-тесте (tests/js/outgoingNumbers.test.js).
 *
 * Правила (ТЗ «Компактные строки»):
 *  - показываем только номера площадки заявки, провайдеры без номеров скрываем;
 *  - выбран может быть один номер среди всех провайдеров;
 *  - «по умолчанию» = outgoing_phones.is_main среди доступных номеров этой площадки.
 *    Если его нет среди доступных, НЕ подставляем первый попавшийся: выбор за оператором;
 *  - SIP-заголовки собираются из выбранного номера и только из него.
 */

// Известные провайдеры: порядок строк в окне и цвет точки. Остальные идут после них.
var PROVIDERS = {
    mango: { name: 'Mango', color: '#ff9c29' },
    beeline: { name: 'Beeline', color: '#f5d722' },
    mts: { name: 'МТС', color: '#f04448' },
    megafon: { name: 'МегаФон', color: '#4dd165' },
    uiscom: { name: 'Uiscom', color: '#a8b5c5' },
};
var PROVIDER_ORDER = ['mango', 'beeline', 'mts', 'megafon', 'uiscom'];
var OTHER_ID = 'other';
var OTHER_NAME = 'Другие';
var DEFAULT_COLOR = '#a8b5c5';

function providerKey(phone) {
    var p = phone && phone.provider != null ? String(phone.provider).trim() : '';
    return p === '' ? OTHER_ID : p.toLowerCase();
}

function providerName(key, sample) {
    if (PROVIDERS[key]) return PROVIDERS[key].name;
    if (key === OTHER_ID) return OTHER_NAME;
    return String(sample.provider).trim();
}

function providerColor(key) {
    return PROVIDERS[key] ? PROVIDERS[key].color : DEFAULT_COLOR;
}

// is_main приходит с сервера как 0/1 (строка или число)
function isMain(phone) {
    return !!(+phone.is_main);
}

/**
 * Телефоны КЦ (ответ /api/call_centres/my/outgoing_phones) → строки провайдеров для окна.
 * Показываем только номера площадки заявки; нет площадки — нет номеров.
 * Порядок номеров внутри провайдера — как отдал сервер.
 *
 * @param {Array} phones
 * @param {number|string|null} platformId
 * @returns {Array<{id:string,name:string,color:string,numbers:Array<{id:number|string,number:string,context:string,isMain:boolean}>}>}
 */
function groupProviders(phones, platformId) {
    if (platformId === null || platformId === undefined) return [];
    var byKey = {};
    var order = [];
    (phones || []).forEach(function (phone) {
        if (!phone || String(phone.platform_id) !== String(platformId)) return;
        var key = providerKey(phone);
        if (!byKey[key]) {
            byKey[key] = { id: key, name: providerName(key, phone), color: providerColor(key), numbers: [] };
            order.push(key);
        }
        byKey[key].numbers.push({
            id: phone.id,
            number: String(phone.number),
            context: phone.asterisk_context == null ? '' : String(phone.asterisk_context),
            isMain: isMain(phone),
        });
    });
    var known = PROVIDER_ORDER.filter(function (key) { return byKey[key]; });
    var rest = order.filter(function (key) { return PROVIDER_ORDER.indexOf(key) === -1 && key !== OTHER_ID; });
    var tail = byKey[OTHER_ID] ? [OTHER_ID] : [];
    return known.concat(rest, tail)
        .map(function (key) { return byKey[key]; })
        .filter(function (p) { return p.numbers.length > 0; });
}

function toSelection(provider, number) {
    return {
        providerId: provider.id,
        providerName: provider.name,
        numberId: number.id,
        number: number.number,
        context: number.context,
    };
}

/**
 * Выбор по id номера. Неизвестный id → null: выбрать то, чего нет в списке, нельзя.
 */
function select(providers, numberId) {
    if (numberId === null || numberId === undefined) return null;
    for (var i = 0; i < providers.length; i++) {
        for (var j = 0; j < providers[i].numbers.length; j++) {
            if (String(providers[i].numbers[j].id) === String(numberId)) {
                return toSelection(providers[i], providers[i].numbers[j]);
            }
        }
    }
    return null;
}

/**
 * Номер «по умолчанию» (is_main) среди доступных. Нет такого → null, выбор за оператором.
 * Если по ошибке данных отмечено несколько — берём первый сверху в окне; выбранный номер
 * оператор видит и в радиокнопке, и в подписи внизу, прежде чем нажать «Позвонить».
 */
function defaultSelection(providers) {
    for (var i = 0; i < providers.length; i++) {
        for (var j = 0; j < providers[i].numbers.length; j++) {
            if (providers[i].numbers[j].isMain) return toSelection(providers[i], providers[i].numbers[j]);
        }
    }
    return null;
}

/**
 * +79057370318 → «+7 905 737-03-18». Маска клиента «+7918****879» → «+7 918 **** 879».
 * Всё, что не похоже на российский номер, возвращаем как есть.
 */
function formatPhone(value) {
    if (value === null || value === undefined) return '';
    var raw = String(value).trim();
    var masked = /^\+?7(\d{3})\*{4}(\d{3})$/.exec(raw.replace(/[\s()-]/g, ''));
    if (masked) return '+7 ' + masked[1] + ' **** ' + masked[2];
    var digits = raw.replace(/\D/g, '');
    if (digits.length === 10) digits = '7' + digits;
    if (raw.indexOf('*') === -1 && digits.length === 11 && digits.charAt(0) === '7') {
        return '+7 ' + digits.slice(1, 4) + ' ' + digits.slice(4, 7) + '-' + digits.slice(7, 9) + '-' + digits.slice(9);
    }
    return raw;
}

var NOTHING_SELECTED = 'Выберите исходящий номер';

/** Подпись внизу окна: «С номера: +7 905 737-03-18 · Beeline» */
function summary(selection) {
    if (!selection) return NOTHING_SELECTED;
    return 'С номера: ' + formatPhone(selection.number) + ' · ' + selection.providerName;
}

/**
 * Заголовки INVITE, по которым воркер исходящих (CallServerWorkerOutgoing) находит номер:
 * X-Custom-Context + X-Custom-Outgoing-Phone-Id — это ровно выбранный номер.
 * Без выбранного номера (или без контекста) звонок не собираем: воркер всё равно повесил бы трубку.
 */
function callHeaders(selection, requestId, sipLogin, userId) {
    if (!selection || selection.numberId === null || selection.numberId === undefined || !selection.context) {
        throw new Error('Не выбран исходящий номер');
    }
    return [
        'X-Custom-Context:' + selection.context,
        'X-Custom-Outgoing-Phone-Id:' + selection.numberId,
        'X-Custom-Request-Id:' + requestId,
        'X-Custom-Sip-Login:' + sipLogin,
        'X-Custom-User-Id:' + userId,
    ];
}

module.exports = {
    PROVIDERS: PROVIDERS,
    NOTHING_SELECTED: NOTHING_SELECTED,
    groupProviders: groupProviders,
    select: select,
    defaultSelection: defaultSelection,
    formatPhone: formatPhone,
    summary: summary,
    callHeaders: callHeaders,
};
